import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ClientLayout from "../../../layouts/ClientLayout";
import { authService } from "../../../lib/api/apiService";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ResetClientPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");

  // Étape 1 : Mot de passe oublié (sans token)
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Étape 2 : Nouveau mot de passe (avec token)
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(!!tokenFromUrl);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) {
      const storedUser =
        localStorage.getItem("user") || sessionStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.email) setEmail(parsedUser.email);
        } catch {
          // ignore
        }
      }
      return;
    }

    const validateToken = async () => {
      setTokenLoading(true);
      setError("");
      try {
        const result = await authService.validateResetTokenForClient(tokenFromUrl);
        if (result.success && result.valid && result.email) {
          setTokenValid(true);
          setResetEmail(result.email);
        } else {
          setTokenValid(false);
          setError(result.message || t("reset_password.errors.token_invalid"));
        }
      } catch (err) {
        setTokenValid(false);
        setError(t("reset_password.errors.token_invalid"));
      } finally {
        setTokenLoading(false);
      }
    };

    validateToken();
  }, [tokenFromUrl, t]);

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setForgotSuccess(false);
    try {
      const result = await authService.forgotPassword(email);
      if (result.success) {
        setForgotSuccess(true);
      } else {
        setError(
          result.message ||
            result.error ||
            t("reset_password.errors.send_error")
        );
      }
    } catch (err) {
      console.error("Erreur envoi lien réinitialisation", err);
      setError(t("reset_password.errors.unexpected_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(t("reset_password.errors.password_mismatch"));
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError(t("reset_password.errors.password_min_length"));
      return;
    }
    setResetLoading(true);
    try {
      const result = await authService.resetPasswordWithCode(
        resetEmail,
        tokenFromUrl,
        newPassword
      );
      if (result.success) {
        setResetSuccess(true);
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      } else {
        setError(result.message || t("reset_password.errors.unexpected_error"));
      }
    } catch (err) {
      console.error("Erreur réinitialisation mot de passe", err);
      setError(t("reset_password.errors.unexpected_error"));
    } finally {
      setResetLoading(false);
    }
  };

  const isSetNewPasswordStep = Boolean(tokenFromUrl);

  return (
    <ClientLayout>
      <div className="pt-8">
        <div className="w-full">
          <div
            className="pt-12 pb-20 flex items-center justify-center bg-cover bg-center relative"
            style={{
              backgroundImage: "url('/images/family-6398107_1280.jpg')",
            }}
          >
            <div className="absolute inset-0 bg-[#F6F6F6] opacity-[0.96]" />
            <div className="relative max-w-lg w-full px-8">
              {isSetNewPasswordStep ? (
                <>
                  <h1 className="text-3xl font-poppins font-bold mb-6 flex justify-center whitespace-nowrap">
                    {t("reset_password.set_new.title")}
                  </h1>

                  {tokenLoading && (
                    <div className="mb-4 p-3 bg-gray-100 text-gray-700 rounded-lg text-sm text-center">
                      {t("reset_password.set_new.validating")}
                    </div>
                  )}

                  {tokenValid === false && !tokenLoading && (
                    <>
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                          {error}
                        </div>
                      )}
                      <div
                        onClick={() => navigate("/reset-password")}
                        className="cursor-pointer flex items-center justify-center mt-6 text-center font-medium text-sm text-[#E5502E] hover:underline"
                      >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="ml-2">
                          {t("reset_password.set_new.request_new_link")}
                        </span>
                      </div>
                      <div
                        onClick={() => navigate("/login")}
                        className="cursor-pointer flex items-center justify-center mt-4 text-center font-medium text-sm text-gray-600"
                      >
                        <span className="hover:text-gray-500">
                          {t("reset_password.back_to_login")}
                        </span>
                      </div>
                    </>
                  )}

                  {tokenValid === true && !tokenLoading && (
                    <>
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                          {error}
                        </div>
                      )}
                      {resetSuccess && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                          {t("reset_password.set_new.success")}
                        </div>
                      )}
                      {!resetSuccess && (
                        <>
                          <p className="text-gray-600 text-base py-4 text-center">
                            {t("reset_password.set_new.description")}
                          </p>
                          <form
                            onSubmit={handleResetSubmit}
                            className="space-y-6"
                          >
                            <div>
                              <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                                {t("reset_password.set_new.email_label")}
                              </label>
                              <input
                                type="email"
                                value={resetEmail}
                                readOnly
                                className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                                {t("reset_password.set_new.new_password_label")}
                              </label>
                              <div className="relative">
                                <input
                                  type={showPasswords ? "text" : "password"}
                                  value={newPassword}
                                  onChange={(e) => setNewPassword(e.target.value)}
                                  required
                                  minLength={6}
                                  className="w-full border rounded-lg px-3 py-2 pr-11 focus:ring-2 focus:ring-[#E5502E] outline-none"
                                  placeholder="••••••••"
                                />
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  onClick={() =>
                                    setShowPasswords((prev) => !prev)
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0"
                                  aria-label={
                                    showPasswords
                                      ? t("reset_password.set_new.hide_password")
                                      : t("reset_password.set_new.show_password")
                                  }
                                >
                                  {showPasswords ? (
                                    <EyeOff className="w-5 h-5" />
                                  ) : (
                                    <Eye className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                                {t("reset_password.set_new.confirm_password_label")}
                              </label>
                              <div className="relative">
                                <input
                                  type={showPasswords ? "text" : "password"}
                                  value={confirmPassword}
                                  onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                  }
                                  required
                                  minLength={6}
                                  className="w-full border rounded-lg px-3 py-2 pr-11 focus:ring-2 focus:ring-[#E5502E] outline-none"
                                  placeholder="••••••••"
                                />
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  onClick={() =>
                                    setShowPasswords((prev) => !prev)
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0"
                                  aria-label={
                                    showPasswords
                                      ? t("reset_password.set_new.hide_password")
                                      : t("reset_password.set_new.show_password")
                                  }
                                >
                                  {showPasswords ? (
                                    <EyeOff className="w-5 h-5" />
                                  ) : (
                                    <Eye className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                            <button
                              type="submit"
                              disabled={resetLoading}
                              className="mt-6 w-full bg-[#E5502E] text-white py-2 rounded-full hover:bg-[#cc4527] transition font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                              {resetLoading
                                ? t("reset_password.set_new.submitting")
                                : t("reset_password.set_new.submit")}
                            </button>
                          </form>
                        </>
                      )}
                      {!resetSuccess && (
                        <div
                          onClick={() => navigate("/login")}
                          className="hover:cursor-pointer flex items-center justify-center mt-8 text-center font-medium text-sm text-gray-600 cursor-pointer"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          <span className="ml-2 hover:text-gray-500">
                            {t("reset_password.back_to_login")}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <h1 className="text-3xl font-poppins font-bold mb-6 flex justify-center whitespace-nowrap">
                    {t("reset_password.title")}
                  </h1>

                  {forgotSuccess && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {t("forgot_password.success.email_sent")}
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {!forgotSuccess ? (
                    <>
                      <p className="text-gray-600 text-base py-4 text-center">
                        {t("reset_password.description")}
                      </p>
                      <form onSubmit={handleForgotSubmit} className="space-y-10">
                        <div>
                          <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                            {t("reset_password.email_label")}
                          </label>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full border rounded-lg px-3 p-2 focus:ring-2 focus:ring-[#E5502E] outline-none"
                            placeholder={t("reset_password.email_placeholder")}
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={loading}
                          className="mt-6 w-full bg-[#E5502E] text-white py-2 rounded-full hover:bg-[#cc4527] transition font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {loading
                            ? t("reset_password.sending")
                            : t("reset_password.send_button")}
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="text-center space-y-4 py-4">
                      <div className="text-green-600">
                        <svg
                          className="w-16 h-16 mx-auto mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-600 text-sm">
                        {t("forgot_password.success.message")}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setForgotSuccess(false);
                          setEmail("");
                          setError("");
                        }}
                        className="text-sm text-[#E5502E] hover:text-[#cc4527] font-medium transition-colors"
                      >
                        {t("forgot_password.success.resend")}
                      </button>
                    </div>
                  )}

                  <div
                    onClick={() => navigate("/login")}
                    className="hover:cursor-pointer flex items-center justify-center mt-12 text-center font-medium text-sm text-gray-600 cursor-pointer"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="ml-2 hover:text-gray-500">
                      {t("reset_password.back_to_login")}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
